// Per-tenant AI assistant config (model + provider key) + knowledge-base docs,
// for the signed-in company admin. Scoped to the caller's tenant by the backend.
//
// SECURITY: the API key is WRITE-ONLY. We send it exactly once in the save body
// (HTTPS); the server stores it AES-encrypted and NEVER returns it — reads only
// ever expose the last 4 digits + a "configured" flag. Nothing here logs the key.
import axios from 'axios';
import { attachCookieAuth } from '@/lib/session';

const AUTH_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
const AI_BASE = import.meta.env.VITE_AI_URL || 'http://localhost:8001';

function authed(baseURL: string) {
  const i = axios.create({ baseURL, timeout: 30000, withCredentials: true });
  i.interceptors.request.use(attachCookieAuth);
  return i;
}
const authApi = authed(AUTH_BASE);
const aiApi = authed(AI_BASE);

export type DanaStyle = 'professional' | 'friendly' | 'corporate' | 'government';
export type DanaLanguage = 'english' | 'swahili' | 'auto';

export interface DanaToggles {
  weeklyDigest: boolean;
  birthdayMessages: boolean;
  anniversaryMessages: boolean;
  onboardingNudges: boolean;
  proactiveNotifications: boolean;
}

export interface DanaWorkingHours {
  tz: string;
  start: string; // HH:MM
  end: string; // HH:MM
}

export interface DanaConfig {
  style: DanaStyle;
  language: DanaLanguage;
  replyTo: string | null;
  toggles: DanaToggles;
  workingHours: DanaWorkingHours;
  /** Brand-locked identity — read-only, never tenant-editable. */
  identity: { name: string; title: string; department: string; employeeId: string; email: string };
}

export interface AiConfig {
  provider: string;
  model: string | null;
  configured: boolean;
  managedKey?: boolean; // served by KaziPeople's platform key (no BYOK needed)
  sttKeySet?: boolean; // tenant's own voice-transcription key present
  keyLast4: string | null;
  configuredAt: string | null;
  chatRetentionDays?: number | null; // days Dana keeps chat history; null = platform default
  dana?: DanaConfig;
  localization?: {
    granted: boolean; // platform grant — tenants tune, never grant
    country: string | null; // read-only here
    config: LocalizationConfig | null;
  };
}

export interface LocalizationConfig {
  region?: string;
  primary_language?: 'sw' | 'en';
  secondary_languages?: Array<'sw' | 'en'>;
  code_switching?: boolean;
  regional_lingo?: boolean;
  lingo_strength?: 'low' | 'moderate' | 'strong';
  formality?: 'relaxed' | 'professional_friendly' | 'professional' | 'formal';
  persona_pack?: string;
  persona_version?: string;
}

export const saveLocalizationConfig = (input: LocalizationConfig) =>
  authApi.put<{ granted: boolean; config: LocalizationConfig }>(
    '/api/v1/tenants/ai-config/localization', input).then((r) => r.data);

export const getAiConfig = () =>
  authApi.get<AiConfig>('/api/v1/tenants/ai-config').then((r) => r.data);

export const getDanaConfig = () =>
  authApi.get<DanaConfig>('/api/v1/tenants/dana-config').then((r) => r.data);

export const saveDanaConfig = (input: {
  style?: DanaStyle;
  language?: DanaLanguage;
  replyTo?: string | null;
  toggles?: Partial<DanaToggles>;
  workingHours?: Partial<DanaWorkingHours>;
}) => authApi.put<DanaConfig>('/api/v1/tenants/dana-config', input).then((r) => r.data);

export const saveAiConfig = (input: { provider?: string; model?: string; apiKey?: string; sttKey?: string | null; chatRetentionDays?: number | null }) =>
  authApi.put<AiConfig>('/api/v1/tenants/ai-config', input).then((r) => r.data);

export const clearAiConfig = () =>
  authApi.delete<AiConfig>('/api/v1/tenants/ai-config').then((r) => r.data);

// --- Knowledge base: per-tenant HR documents for RAG (ai-agent-service) ---

export interface HRDoc {
  id: string;
  name: string;
  content_type?: string | null;
  chunk_count: number;
  status: string;
  created_at?: string | null;
}

export const listDocuments = () =>
  aiApi.get<HRDoc[]>('/api/v1/ai/documents').then((r) => r.data);

export const uploadDocument = (file: File) => {
  const form = new FormData();
  form.append('file', file);
  return aiApi
    .post<{ id: string; name: string; chunk_count: number }>('/api/v1/ai/documents', form)
    .then((r) => r.data);
};

export const deleteDocument = (id: string) =>
  aiApi.delete(`/api/v1/ai/documents/${id}`).then((r) => r.data);

// Curated current Claude models (models evolve — a custom field covers new ones).
export const CLAUDE_MODELS: { value: string; label: string }[] = [
  { value: 'claude-opus-4-8', label: 'Claude Opus 4.8 — most capable' },
  { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6 — balanced (recommended)' },
  { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5 — fast & low-cost' },
];

export const OPENAI_MODELS: { value: string; label: string }[] = [
  { value: 'gpt-4o', label: 'GPT-4o — most capable' },
  { value: 'gpt-4o-mini', label: 'GPT-4o mini — fast & low-cost (recommended for voice)' },
];

export const PROVIDERS: { value: 'anthropic' | 'openai'; label: string; keyHint: string }[] = [
  { value: 'anthropic', label: 'Anthropic (Claude)', keyHint: 'sk-ant-…' },
  { value: 'openai', label: 'OpenAI (GPT)', keyHint: 'sk-…' },
];
